import { MODEL_OPTIONS } from '@/lib/modelCatalog';

/**
 * The builder palette: turning what this install actually has into droppable canvas nodes.
 *
 * The node registry only has six types, but "add an LLM node then type gpt-4o-mini into it"
 * is not a palette — it's a form. So each entry is a node type PLUS the config that makes it
 * concrete: one per auto-category tool, one per (configured provider, known model) pair, one
 * per AI-model connection. Dropping `kb_search` gives you a tool node already pointed at
 * kb_search; the inspector is for changing your mind, not for initial setup.
 *
 * Pure and dependency-free so it can be unit tested — every input is data the page already
 * fetched, and the output is plain objects the canvas turns into nodes.
 */

/**
 * The dataTransfer key the palette writes and the canvas reads. Typed rather than
 * 'text/plain' so dragging text in from another application cannot spawn a node.
 *
 * Declared HERE, not in WorkflowCanvas.jsx: the palette is in the main bundle and the canvas
 * is lazily loaded into its own `canvas` chunk, so importing the constant from the canvas
 * would drag @xyflow and its d3 transitives back into the initial download.
 */
export const PALETTE_DRAG_TYPE = 'application/workflow-node';

/** Group order in the panel. Anything with an unlisted category sorts to the end. */
const CATEGORY_ORDER = [
  'Agents',
  'Input / Output',
  'Intelligence',
  'Routing & Logic',
  'Connectors',
  'Tools',
  'Custom Tools',
  'Connections',
];

const TOOL_CATEGORY = { builtin: 'Tools', dynamic: 'Custom Tools' };

/** Company agents as first-class draggable blocks: dropping an agent puts a pre-configured
 *  `agent_turn` node on the canvas with that agent's persona and key already bound. */
function agentEntries(agents = []) {
  return (agents || [])
    .filter((a) => a && a.is_active !== false && !a.is_paused)
    .map((a) => ({
      key: `agent:${a.key}`,
      label: a.name,
      description: a.description || `Specialist agent (${a.key})`,
      category: 'Agents',
      icon: 'bot',
      nodeType: 'agent_turn',
      data: {
        label: a.name,
        agent_key: a.key,
        prompt: '',
        max_tool_loops: 10,
      },
    }));
}

/** Entries built from node specs alone — the generic "blank node of type X". */
function nodeTypeEntries(nodeCatalog) {
  return nodeCatalog.map((spec) => ({
    key: `type:${spec.type}`,
    label: spec.label || spec.type,
    description: spec.description || '',
    category: spec.category || 'Other',
    icon: spec.icon || 'box',
    nodeType: spec.type,
    data: {},
  }));
}

/** One tool node per callable tool. `arguments_json` is seeded with `{}` because the node
 *  parses it as JSON after template substitution — an empty string would throw at run time. */
function toolEntries(toolOptions) {
  return toolOptions.map((tool) => ({
    key: `tool:${tool.name}`,
    label: tool.name,
    description: tool.description || '',
    category: TOOL_CATEGORY[tool.source] || 'Tools',
    icon: tool.source === 'dynamic' ? 'puzzle' : 'wrench',
    nodeType: 'tool',
    data: { label: tool.name, tool_name: tool.name, arguments_json: '{}' },
  }));
}

/** One palette entry per connector package (services/chat/tools/connectors.js), NOT per
 *  tool — a connector owns several tools, and grouping them lets the author "attach a
 *  connector" by drag rather than hunting through a flat 74-tool list. Dropping an entry
 *  places a `tool` node with its connector pre-scoped; the inspector's Tool dropdown then
 *  offers only that connector's tools (toolOptions carry a `connector` tag). */
function connectorEntries(connectors) {
  return (connectors || []).map((c) => ({
    key: `connector:${c.key}`,
    label: c.label,
    description: c.description || `${(c.tools || []).length} tool(s)`,
    category: 'Connectors',
    icon: c.icon || 'puzzle',
    nodeType: 'tool',
    data: { label: c.label, connector: c.key, tool_name: '', arguments_json: '{}' },
  }));
}

/** Selects primary studio models so a provider with 90+ snapshot tags (e.g. OpenAI)
 *  does not flood the palette with redundant entries while keeping the floor intact. */
function selectPaletteModels(providerKey, modelsByProvider) {
  const discovered = modelsByProvider[providerKey];
  const floor = MODEL_OPTIONS[providerKey] || [];
  if (!discovered || !discovered.length) return floor;
  const preferred = floor.filter((m) => discovered.includes(m));
  if (preferred.length >= 2) return preferred;
  return discovered.slice(0, 4);
}

/** One LLM node per curated model of a configured provider. Unconfigured providers are
 *  skipped — the inspector's provider dropdown still lists every provider with status. */
function modelEntries(providers, modelsByProvider = {}) {
  return providers
    .filter((p) => p.configured)
    .flatMap((p) => selectPaletteModels(p.key, modelsByProvider).map((model) => ({
      key: `model:${p.key}:${model}`,
      label: model,
      description: `LLM call via ${p.label}`,
      category: 'Intelligence',
      icon: 'sparkles',
      nodeType: 'llm',
      data: { label: model, provider: p.key, model },
    })));
}

/** One LLM node per AI-model connection — the company's own API key rather than the shared
 *  env one. `connection_id` is read by services/workflows/nodes/index.js's callModel. */
function connectionEntries(connections) {
  return connections
    .filter((c) => c.category_key === 'ai-model' && c.is_active !== false && c.is_active !== 0)
    .map((c) => ({
      key: `connection:${c.id}`,
      label: c.name,
      description: `LLM call using the "${c.name}" connection (${c.provider})`,
      category: 'Connections',
      icon: 'plug',
      nodeType: 'llm',
      data: { label: c.name, provider: c.provider, connection_id: c.id },
    }));
}

export function buildPalette({
  nodeCatalog = [], toolOptions = [], providers = [], connections = [], modelsByProvider = {},
  agents = [], connectors = [],
} = {}) {
  return [
    ...agentEntries(agents),
    ...nodeTypeEntries(nodeCatalog),
    ...modelEntries(providers, modelsByProvider),
    ...connectionEntries(connections),
    ...connectorEntries(connectors),
    ...toolEntries(toolOptions),
  ];
}

/** Case-insensitive substring match across the fields a user would search by. Same
 *  all-terms-must-match idiom as components/dataTableFilter.js, so typing "kb search"
 *  finds kb_search even though the underscore isn't typed. */
export function filterPalette(entries, query) {
  const terms = String(query || '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return entries;
  return entries.filter((e) => {
    const haystack = `${e.label} ${e.description} ${e.category}`.toLowerCase();
    return terms.every((t) => haystack.includes(t));
  });
}

/** Ordered `[{ category, entries }]` for the accordion. Empty categories are dropped so a
 *  search never leaves a row of zero-count headers behind. */
export function groupPalette(entries) {
  const groups = new Map();
  for (const entry of entries) {
    if (!groups.has(entry.category)) groups.set(entry.category, []);
    groups.get(entry.category).push(entry);
  }
  const rank = (name) => {
    const i = CATEGORY_ORDER.indexOf(name);
    return i === -1 ? CATEGORY_ORDER.length : i;
  };
  return [...groups.entries()]
    .sort((a, b) => rank(a[0]) - rank(b[0]) || a[0].localeCompare(b[0]))
    .map(([category, items]) => ({ category, entries: items }));
}

/* ------------------------------------------------------------------ node card summary -- */

const truncate = (text, max = 44) => {
  const s = String(text || '').replace(/\s+/g, ' ').trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
};

/**
 * The one or two lines a node card shows under its title — enough to read a graph without
 * clicking every node, while the full config stays in the inspector.
 *
 * Returns `[{ text, tone }]`; `tone: 'warn'` marks config that is missing rather than merely
 * unset, so an unfinished node is visible on the canvas instead of only at save time.
 */
/** One line, warned when the value it reports is missing rather than merely blank. */
const line = (value, placeholder) => ({
  text: truncate(value) || placeholder,
  tone: value ? 'muted' : 'warn',
});

/** Exported for the congruence test in lib/workflowNodeVisuals.test.js: presence of a KEY
 *  is the invariant, not a non-empty result — `exit` deliberately renders no line when it
 *  carries no reason. */
export const SUMMARIES = {
  llm: (d) => [
    { text: `${d.model || 'no model'} · ${d.connection_id ? 'connection' : (d.provider || 'openai')}`, tone: d.model ? 'muted' : 'warn' },
    line(d.prompt, 'no prompt'),
    ...(d.output_schema ? [{ text: 'structured output', tone: 'muted' }] : []),
  ],
  condition: (d) => [d.mode === 'llm' ? line(d.question, 'no question') : line(d.expression, 'no expression')],
  tool: (d) => [line(d.tool_name, 'no tool selected')],
  agent_turn: (d) => [
    line(d.agent_key, 'no agent selected'),
    line(d.prompt, 'no prompt'),
    ...(d.output_schema ? [{ text: 'structured output', tone: 'muted' }] : []),
  ],
  image_gen: (d) => [
    { text: `${d.model || 'gpt-image-1'} · ${d.size || 'auto'}`, tone: 'muted' },
    line(d.prompt, 'no prompt'),
  ],
  http_request: (d) => [
    { text: `${String(d.method || 'GET').toUpperCase()}${d.allow_private_hosts ? ' · private hosts' : ''}`, tone: 'muted' },
    line(d.url, 'no URL'),
  ],
  a2a_call: (d) => [
    { text: d.provider_slug ? `${d.provider_slug}_ask` : 'no provider', tone: d.provider_slug ? 'muted' : 'warn' },
    line(d.message_template, 'no message'),
  ],
  sub_workflow: (d) => [line(d.workflow_key, 'no workflow selected')],
  iterator: (d) => [
    line(d.items_path, 'no items path'),
    line(d.workflow_key, 'no workflow selected'),
  ],
  delay: (d) => [{
    text: d.duration_seconds ? `wait ${d.duration_seconds} ${d.unit || 'seconds'}` : 'no duration',
    tone: d.duration_seconds ? 'muted' : 'warn',
  }],
  transform: (d) => [
    line(d.function, 'no function selected'),
    line(d.value_path, 'no input path'),
  ],
  // `value_path` is read here for the same reason outputNodeValue() reads it second
  // (services/workflows/nodes/index.js): template wins, then value_path, then the whole
  // scope. The card used to check only `template`, so a node returning exactly
  // `normalize.output` advertised itself as returning EVERY node's output — the summary
  // was not merely vague, it was wrong.
  output: (d) => [{
    text: truncate(d.template) || (d.value_path ? `returns ${d.value_path}` : 'returns every node output'),
    tone: 'muted',
  }],
  exit: (d) => (d.reason ? [{ text: truncate(d.reason), tone: 'muted' }] : []),
  input: () => [{ text: 'run input enters here', tone: 'muted' }],
};

export function summaryFor(type, data = {}) {
  return (SUMMARIES[type] || (() => []))(data);
}
